import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Key, CheckCircle2, AlertCircle, RefreshCw, Trash2, Loader2,
  Eye, EyeOff, ExternalLink,
} from "lucide-react";
import { Meta } from "./OpenAIAdsHelpers";

export default function OpenAIAdsConnectionPanel({
  clientId, connection, onRefresh, onAfterSync,
}) {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const testConnection = async () => {
    if (!apiKey) { toast.error("Enter the OpenAI Ads API key first"); return; }
    setTesting(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/openai-ads/connect`, {
        api_key: apiKey, test_only: true,
      });
      toast.success(`Verified · ${data.account?.name || "OpenAI Ads account"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection test failed");
    } finally { setTesting(false); }
  };

  const saveConnection = async () => {
    if (!apiKey) { toast.error("Enter the OpenAI Ads API key first"); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/openai-ads/connect`, {
        api_key: apiKey, test_only: false,
      });
      toast.success("OpenAI Ads account connected");
      setApiKey("");
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save connection");
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    try {
      await api.delete(`/clients/${clientId}/openai-ads/disconnect`);
      toast.success("Disconnected");
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect");
    }
  };

  const syncHierarchy = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/openai-ads/sync/hierarchy`);
      toast.success(`Synced ${data.campaigns} campaigns · ${data.ad_groups} ad groups · ${data.ads} ads`);
      await onAfterSync();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="openai-ads-connection-card">
      <CardContent className="p-5 space-y-4">
        {connection.connected ? (
          <>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200"
                  data-testid="openai-ads-status">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  {connection.status === "active" ? "Connected" : (connection.status || "Connected")}
                </Badge>
                <div>
                  <div className="font-display font-bold text-xl tracking-tight">
                    {connection.account_name || "Unnamed account"}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                    {connection.openai_ad_account_id}
                    {connection.preview_url && (
                      <>
                        {" · "}
                        <a href={connection.preview_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-0.5 hover:text-[#10A37F]">
                          preview <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="rounded-sm"
                  disabled={syncing} onClick={syncHierarchy}
                  data-testid="openai-ads-sync-hierarchy">
                  {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                           : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Sync now
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                      data-testid="openai-ads-disconnect">
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Disconnect
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-sm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect this OpenAI Ads account?</AlertDialogTitle>
                      <AlertDialogDescription>
                        The encrypted API key will be deleted. Imported campaigns,
                        ad groups, ads and insights stay in the database for audit;
                        you can clear them manually if needed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                        onClick={disconnect}
                        data-testid="openai-ads-disconnect-confirm">
                        Disconnect
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-zinc-100 text-[11px]">
              <Meta label="Timezone" value={connection.timezone || "—"} />
              <Meta label="Currency" value={connection.currency_code || "—"} />
              <Meta label="Last synced"
                value={connection.last_sync_completed_at
                  ? new Date(connection.last_sync_completed_at).toLocaleString()
                  : "Never"} />
              <Meta label="Status" value={connection.status || "—"}
                tone={connection.status === "error" ? "red" : "default"} />
            </div>
            {connection.last_error && (
              <div className="rounded-sm border border-red-200 bg-red-50 p-2.5 flex items-start gap-2 text-[12px]"
                data-testid="openai-ads-last-error">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <div className="font-mono text-red-800 break-words">{connection.last_error}</div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 text-zinc-500" />
              <div className="label-overline">Connect this client&apos;s OpenAI Ads account</div>
            </div>
            <p className="text-xs text-zinc-600">
              Paste the client&apos;s <strong>OpenAI Ads API key</strong>. We store it encrypted
              and never expose it to the browser after save.
            </p>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[280px] space-y-1.5">
                <Label className="label-overline">OpenAI Ads API key</Label>
                <div className="relative">
                  <Input
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="rounded-sm font-mono text-sm pr-9"
                    data-testid="openai-ads-key-input"
                  />
                  <button type="button"
                    onClick={() => setShowKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                    data-testid="openai-ads-key-toggle">
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button variant="outline" className="rounded-sm"
                disabled={testing || !apiKey} onClick={testConnection}
                data-testid="openai-ads-test-connection">
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                Test connection
              </Button>
              <Button className="rounded-sm bg-[#10A37F] hover:bg-[#0d8a6b] text-white"
                disabled={saving || !apiKey} onClick={saveConnection}
                data-testid="openai-ads-save-connection">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                Connect
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
